import { useEffect, type ReactNode } from "react";

export type Tone = "primary" | "accent" | "secondary" | "neutral" | "danger";

const TONE_BADGE: Record<Tone, string> = {
  primary: "bg-primary-100 text-primary-700 border-primary-300",
  accent: "bg-accent-100 text-accent-900 border-accent-300",
  secondary: "bg-secondary-100 text-secondary-700 border-secondary-300",
  neutral: "bg-background-200 text-foreground-600 border-background-300",
  danger: "bg-accent-200 text-accent-950 border-accent-400",
};

const TONE_BUBBLE: Record<Tone, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-900",
  secondary: "bg-secondary-100 text-secondary-700",
  neutral: "bg-background-200 text-foreground-600",
  danger: "bg-accent-100 text-accent-900",
};

const TONE_DOT: Record<Tone, string> = {
  primary: "bg-primary-500",
  accent: "bg-accent-500",
  secondary: "bg-secondary-500",
  neutral: "bg-foreground-400",
  danger: "bg-accent-600",
};

export const btnPrimary =
  "inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60";
export const btnAccent =
  "inline-flex items-center gap-2 bg-accent-500 text-accent-950 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60";
export const btnGhost =
  "inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60";

export function StatusPill({
  label,
  tone = "neutral",
  withDot = true,
}: {
  label: string;
  tone?: Tone;
  withDot?: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${TONE_BADGE[tone]}`}
    >
      {withDot ? <span className={`w-1.5 h-1.5 rounded-full ${TONE_DOT[tone]}`}></span> : null}
      {label}
    </span>
  );
}

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 md:mb-8">
      <div>
        {eyebrow ? (
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-700">
            {eyebrow}
          </span>
        ) : null}
        <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold mt-2">
          {title}
        </h1>
        {subtitle ? (
          <p className="text-sm md:text-base text-foreground-600 mt-2 max-w-2xl">{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div className="flex items-center gap-2 flex-wrap">{actions}</div> : null}
    </div>
  );
}

export interface Stat {
  label: string;
  value: ReactNode;
  icon: string;
  tone?: Tone;
  hint?: string;
}

export function StatCards({ stats }: { stats: Stat[] }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5"
        >
          <span
            className={`w-10 h-10 flex items-center justify-center rounded-full ${TONE_BUBBLE[stat.tone || "primary"]}`}
          >
            <i className={`${stat.icon} text-xl`}></i>
          </span>
          <p className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-4 truncate">
            {stat.value}
          </p>
          <p className="text-xs md:text-sm text-foreground-600 mt-1">{stat.label}</p>
          {stat.hint ? <p className="text-[11px] text-foreground-500 mt-0.5">{stat.hint}</p> : null}
        </div>
      ))}
    </div>
  );
}

export function FilterBar({ children }: { children: ReactNode }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5 flex flex-col lg:flex-row lg:items-center gap-3">
      {children}
    </div>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative flex-1 min-w-0">
      <span className="w-5 h-5 flex items-center justify-center absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
        <i className="ri-search-line text-sm"></i>
      </span>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full bg-background-100 border border-background-300 rounded-md pl-11 pr-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
      />
    </div>
  );
}

export function Select({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  label?: string;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-foreground-700">
      {label ? <span className="hidden sm:inline whitespace-nowrap">{label}</span> : null}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="bg-background-100 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export interface Column<T> {
  key: string;
  header: string;
  primary?: boolean;
  className?: string;
  render: (row: T) => ReactNode;
}

const selectBoxCls = (checked: boolean) =>
  `w-5 h-5 flex items-center justify-center rounded border transition-colors cursor-pointer shrink-0 ${
    checked
      ? "bg-primary-500 border-primary-500 text-background-50"
      : "border-background-300 text-foreground-400 hover:border-primary-400"
  }`;

export function DataTable<T extends { id: number }>({
  columns,
  rows,
  onRowClick,
  rowActions,
  selectable = false,
  selectedIds = [],
  onToggleSelect,
  onToggleSelectAll,
  allSelected = false,
}: {
  columns: Column<T>[];
  rows: T[];
  onRowClick?: (row: T) => void;
  /** Per-row quick actions rendered outside the clickable row area. */
  rowActions?: (row: T) => ReactNode;
  selectable?: boolean;
  selectedIds?: number[];
  onToggleSelect?: (id: number) => void;
  onToggleSelectAll?: () => void;
  allSelected?: boolean;
}) {
  const primaryCol = columns.find((col) => col.primary) || columns[0];
  const otherCols = columns.filter((col) => col !== primaryCol);
  const selectedSet = new Set(selectedIds);

  return (
    <>
      <div className="hidden md:block bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-background-100 border-b border-background-200">
                {selectable ? (
                  <th className="px-4 py-3.5 w-12">
                    <button
                      type="button"
                      onClick={onToggleSelectAll}
                      aria-label={allSelected ? "Clear selection" : "Select all"}
                      className={selectBoxCls(allSelected)}
                    >
                      <i className={allSelected ? "ri-checkbox-line" : "ri-checkbox-blank-line"}></i>
                    </button>
                  </th>
                ) : null}
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500 whitespace-nowrap"
                  >
                    {col.header}
                  </th>
                ))}
                {rowActions ? (
                  <th className="px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500 whitespace-nowrap text-right">
                    Actions
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row)}
                  className={`hover:bg-background-100 transition-colors align-top ${
                    onRowClick ? "cursor-pointer" : ""
                  }`}
                >
                  {selectable ? (
                    <td className="px-4 py-4">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onToggleSelect?.(row.id);
                        }}
                        aria-label={selectedSet.has(row.id) ? "Deselect row" : "Select row"}
                        className={selectBoxCls(selectedSet.has(row.id))}
                      >
                        <i className={selectedSet.has(row.id) ? "ri-checkbox-line" : "ri-checkbox-blank-line"}></i>
                      </button>
                    </td>
                  ) : null}
                  {columns.map((col) => (
                    <td key={col.key} className={`px-4 py-4 ${col.className || ""}`}>
                      {col.render(row)}
                    </td>
                  ))}
                  {rowActions ? (
                    <td
                      className="px-4 py-4 text-right"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="inline-flex items-center justify-end">{rowActions(row)}</div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="md:hidden space-y-3">
        {rows.map((row) => (
          <div key={row.id} className="bg-background-50 border border-background-200 rounded-lg p-4">
            {selectable ? (
              <div className="flex items-center gap-2 mb-3 pb-3 border-b border-background-200">
                <button
                  type="button"
                  onClick={() => onToggleSelect?.(row.id)}
                  aria-label={selectedSet.has(row.id) ? "Deselect row" : "Select row"}
                  className={selectBoxCls(selectedSet.has(row.id))}
                >
                  <i className={selectedSet.has(row.id) ? "ri-checkbox-line" : "ri-checkbox-blank-line"}></i>
                </button>
                <span className="text-xs font-semibold text-foreground-500">
                  {selectedSet.has(row.id) ? "Selected" : "Tap to select"}
                </span>
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => onRowClick?.(row)}
              className="w-full text-left cursor-pointer"
            >
              <div className="mb-3">{primaryCol.render(row)}</div>
              <dl className="space-y-1.5">
                {otherCols.map((col) => (
                  <div key={col.key} className="flex items-start justify-between gap-3">
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 shrink-0">
                      {col.header}
                    </dt>
                    <dd className="text-sm text-foreground-800 text-right">{col.render(row)}</dd>
                  </div>
                ))}
              </dl>
            </button>
            {rowActions ? (
              <div className="mt-3 pt-3 border-t border-background-200 flex justify-end">
                {rowActions(row)}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </>
  );
}

export function EmptyState({
  icon,
  title,
  message,
}: {
  icon: string;
  title: string;
  message: string;
}) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg py-16 px-6 text-center">
      <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
        <i className={`${icon} text-3xl`}></i>
      </span>
      <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">{title}</h3>
      <p className="text-foreground-600 mt-3 max-w-md mx-auto">{message}</p>
    </div>
  );
}

export function LoadingState({ label }: { label: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg py-20 flex flex-col items-center gap-3">
      <i className="ri-loader-4-line text-3xl animate-spin text-primary-500"></i>
      <p className="text-sm text-foreground-600">{label}</p>
    </div>
  );
}

export function ErrorBanner({
  message,
  onRetry,
  onDismiss,
}: {
  message: string;
  onRetry?: () => void;
  onDismiss?: () => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-accent-100 border border-accent-300 rounded-lg px-4 py-3.5">
      <p className="flex items-start gap-2 text-sm text-accent-900 flex-1">
        <i className="ri-error-warning-line text-lg"></i>
        <span>{message}</span>
      </p>
      {onRetry ? (
        <button type="button" onClick={onRetry} className={btnAccent}>
          <i className="ri-refresh-line"></i> Retry
        </button>
      ) : null}
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm font-semibold text-accent-900 underline whitespace-nowrap cursor-pointer self-start"
        >
          Dismiss
        </button>
      ) : null}
    </div>
  );
}

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[110]">
      <div className="absolute inset-0 bg-foreground-950/60" onClick={onClose} aria-hidden="true"></div>
      <aside
        className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-background-50 border-l border-background-200 flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between gap-3 px-5 md:px-6 py-4 border-b border-background-200">
          <div className="min-w-0">
            {subtitle ? (
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
                {subtitle}
              </p>
            ) : null}
            <h2 className="font-heading text-xl text-foreground-950 font-semibold truncate">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-5">{children}</div>
        {footer ? <div className="px-5 md:px-6 py-4 border-t border-background-200">{footer}</div> : null}
      </aside>
    </div>
  );
}

export function DetailList({
  items,
}: {
  items: { icon: string; label: string; value: ReactNode }[];
}) {
  return (
    <div className="space-y-4">
      {items.map((item) => (
        <div key={item.label} className="flex items-start gap-3">
          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-primary-700 shrink-0">
            <i className={`${item.icon} text-sm`}></i>
          </span>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
              {item.label}
            </p>
            <p className="text-sm text-foreground-900 mt-0.5 break-words">{item.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">
        {label}
      </p>
      {children}
    </div>
  );
}

export const fieldInput =
  "w-full bg-background-100 border border-background-300 rounded-md px-3 py-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer";

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = "md",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: "md" | "lg";
}) {
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[115] flex items-start sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60" onClick={onClose} aria-hidden="true"></div>
      <div
        className={`relative w-full ${size === "lg" ? "sm:max-w-3xl" : "sm:max-w-xl"} max-h-[90vh] bg-background-50 border border-background-200 rounded-lg flex flex-col`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between gap-3 px-5 md:px-6 py-4 border-b border-background-200">
          <div className="min-w-0">
            {subtitle ? (
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
                {subtitle}
              </p>
            ) : null}
            <h2 className="font-heading text-xl text-foreground-950 font-semibold truncate">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-4">{children}</div>
        {footer ? (
          <div className="px-5 md:px-6 py-4 border-t border-background-200 flex items-center justify-end gap-2 flex-wrap">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function TextField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

export const textInput =
  "w-full bg-background-100 border border-background-300 rounded-md px-3 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400";